export type AnalyticsAttribution = {
  referrerDomain: string | null;
  source: string;
  channel: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  trackingLink: string | null;
};

const text = (value: unknown, max = 180) => typeof value === "string" && value.trim()
  ? value.trim().slice(0, max)
  : null;

export const SESSION_ANALYTICS_ATTRIBUTION_KEY = "teravia:analytics-attribution:v1";

export function currentSessionAnalyticsAttribution(): AnalyticsAttribution {
  if (typeof window === "undefined") return attributionFromValues({});
  try {
    const stored = window.sessionStorage.getItem(SESSION_ANALYTICS_ATTRIBUTION_KEY);
    if (stored) return JSON.parse(stored) as AnalyticsAttribution;
    const params = new URLSearchParams(window.location.search);
    const attribution = attributionFromValues({
      referrer: document.referrer, utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"), utm_content: params.get("utm_content"),
      channel: params.get("channel"), tracking_link: params.get("tl"),
    }, window.location.hostname.replace(/^www\./, ""));
    window.sessionStorage.setItem(SESSION_ANALYTICS_ATTRIBUTION_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return attributionFromValues({ referrer: document.referrer }, window.location.hostname.replace(/^www\./, ""));
  }
}

export function thirdPartyReferrerDomain(value: unknown, siteHost = "teravia.app") {
  const raw = text(value, 2000);
  if (!raw) return null;
  try {
    const host = new URL(raw).hostname.toLowerCase().replace(/^www\./, "");
    if (!host || host === siteHost || host.endsWith(`.${siteHost}`)) return null;
    return host;
  } catch {
    return null;
  }
}

export function normalizeAnalyticsSource(value: unknown): string {
  const raw = text(value)?.toLowerCase().replace(/^www\./, "") || "";
  if (!raw) return "Direct / unknown";
  if (["facebook", "facebook-group", "facebook-page"].includes(raw) || /(^|\.)((l|m|lm)\.)?facebook\.com$/.test(raw)) return "Facebook";
  if (["threads"].includes(raw) || /^(l\.)?threads\.(net|com)$/.test(raw)) return "Threads";
  if (["instagram"].includes(raw) || /^(l\.)?instagram\.com$/.test(raw)) return "Instagram";
  if (["x", "twitter"].includes(raw) || raw === "t.co" || /(^|\.)(x|twitter)\.com$/.test(raw)) return "X";
  if (["tiktok"].includes(raw) || /(^|\.)tiktok\.com$/.test(raw)) return "TikTok";
  if (["youtube"].includes(raw) || raw === "youtu.be" || /(^|\.)youtube\.com$/.test(raw)) return "YouTube";
  if (["google"].includes(raw) || /(^|\.)google\.[a-z.]+$/.test(raw)) return "Google";
  if (["bing"].includes(raw) || /(^|\.)bing\.com$/.test(raw)) return "Bing";
  if (["chatgpt", "perplexity", "ai-assistants"].includes(raw) || raw === "chatgpt.com" || raw === "perplexity.ai") return "AI assistants";
  if (["partner", "partner-qr"].includes(raw)) return "Partner";
  if (["leaflet", "business-card"].includes(raw)) return "Offline";
  if (["email", "email-outreach"].includes(raw)) return "Email";
  return raw.replace(/^www\./, "");
}

export function attributionFromValues(input: Record<string, unknown>, siteHost = "teravia.app"): AnalyticsAttribution {
  const referrerDomain = thirdPartyReferrerDomain(input.referrerDomain || input.referrer, siteHost);
  const utmSource = text(input.utmSource ?? input.utm_source);
  const utmMedium = text(input.utmMedium ?? input.utm_medium);
  const utmCampaign = text(input.utmCampaign ?? input.utm_campaign);
  const utmContent = text(input.utmContent ?? input.utm_content);
  const trackingLink = text(input.trackingLink ?? input.tracking_link, 120);
  const channel = text(input.channel, 100) || (utmSource && /^(facebook-group|facebook-page|threads|instagram|x|tiktok|youtube|email-outreach|partner-qr|leaflet|business-card)$/i.test(utmSource) ? utmSource.toLowerCase() : null);
  return {
    referrerDomain,
    source: normalizeAnalyticsSource(utmSource || referrerDomain),
    channel,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    trackingLink,
  };
}

export function isAnalyticsBot(userAgent: unknown) {
  const ua = text(userAgent, 2000)?.toLowerCase() || "";
  return /(?:\bbot\b|spider|crawler|headless|facebookexternalhit|twitterbot|slackbot|whatsapp|googlebot|bingbot|gptbot|claudebot|perplexitybot|python-requests|\bcurl\b|teraviacontentmap)/i.test(ua);
}

export function isClearlyInternalAnalyticsTraffic(path: unknown, host: unknown) {
  const safePath = text(path, 1000) || "/";
  const safeHost = text(host, 253)?.toLowerCase() || "";
  return safePath === "/admin" || safePath.startsWith("/admin/") || safeHost === "localhost" || safeHost.startsWith("localhost:") || safeHost === "127.0.0.1" || safeHost.startsWith("127.0.0.1:");
}
